<template>
  <div class="pa-4 pa-md-6">
    <div class="grey--text" style="font-size: 13px">Otso Panalo Game</div>
    <h2 class="font-weight-bold mb-3 mb-md-5">Game Configs</h2>

    <v-card class="shadow">
      <v-card-title>
        <v-row justify="space-between">
          <v-col cols="12" sm="6" md="4" xl="3" align-self="end">
            <v-btn
              v-if="canManage"
              color="primary"
              depressed
              class="mr-2"
              @click="openCreateDialog"
            >
              <v-icon left>mdi-plus</v-icon>
              New Config
            </v-btn>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="secondary"
                  v-bind="attrs"
                  v-on="on"
                  @click="loadRecords"
                >
                  <v-icon>mdi-cached</v-icon>
                </v-btn>
              </template>
              <span>Refresh</span>
            </v-tooltip>
          </v-col>

          <v-col cols="12" sm="6" md="4" xl="3">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              placeholder="Search"
              single-line
              hide-details="auto"
              :disabled="loading"
              clearable
              outlined
              dense
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="records"
          :search="search"
          :loading="loading"
          :items-per-page="10"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          loading-text="Loading, please wait a moment"
        >
          <template v-slot:[`item.action`]="{ item }">
            <v-tooltip right v-if="canManage">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  small
                  color="primary"
                  @click="openEditDialog(item)"
                >
                  <v-icon size="large">mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>Edit</span>
            </v-tooltip>
          </template>

          <template v-slot:[`item.logo`]="{ item }">
            <v-avatar size="42" tile color="grey lighten-4">
              <v-img v-if="item.logo" :src="item.logo" contain></v-img>
              <v-icon v-else color="grey">mdi-image-off-outline</v-icon>
            </v-avatar>
          </template>

          <template v-slot:[`item.bet_amount`]="{ item }">
            <span>{{ moneyFormat(item.bet_amount) }}</span>
          </template>

          <template v-slot:[`item.jackpot_prize`]="{ item }">
            <span>{{ moneyFormat(item.jackpot_prize) }}</span>
          </template>

          <template v-slot:[`item.second_prize`]="{ item }">
            <span>{{ moneyFormat(item.second_prize) }}</span>
          </template>

          <template v-slot:[`item.third_prize`]="{ item }">
            <span>{{ moneyFormat(item.third_prize) }}</span>
          </template>

          <template v-slot:[`item.fourth_prize`]="{ item }">
            <span>{{ moneyFormat(item.fourth_prize) }}</span>
          </template>

          <template v-slot:[`item.pool_percentage`]="{ item }">
            <span>{{ percentFormat(item.pool_percentage) }}</span>
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              small
              :class="
                item.status === 'Active'
                  ? 'green lighten-5 green--text'
                  : 'orange lighten-5 orange--text'
              "
            >
              {{ item.status }}
            </v-chip>
          </template>

          <template v-slot:[`item.updated_at`]="{ item }">
            <span>{{ formatTheDate(item.updated_at || item.created_at) }}</span>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="dialog" max-width="760" persistent>
      <v-card>
        <v-card-title>
          {{ isEditing ? "Update Config" : "New Config" }}

          <v-spacer></v-spacer>

          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="py-4">
          <v-alert v-if="formError" type="error" dense outlined class="mb-4">
            {{ formError }}
          </v-alert>

          <v-form
            ref="form"
            v-model="valid"
            lazy-validation
            @submit.prevent="submitForm"
          >
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.name"
                  label="Name"
                  :rules="[
                    rules.required,
                    rules.minimum(2),
                    rules.maximum(255),
                  ]"
                  hide-details="auto"
                  outlined
                  dense
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="form.status"
                  :items="statusOptions"
                  label="Status"
                  :rules="[rules.required]"
                  hide-details="auto"
                  outlined
                  dense
                ></v-select>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="7">
                <v-file-input
                  v-model="form.logo"
                  label="Logo"
                  accept="image/png, image/jpeg, image/jpg, image/bmp, image/svg+xml"
                  prepend-icon=""
                  append-icon="mdi-open-in-app"
                  show-size
                  hide-details="auto"
                  outlined
                  dense
                  @change="onLogoChange"
                  @click:clear="clearLogoPreview"
                ></v-file-input>
              </v-col>

              <v-col cols="12" md="5" class="d-flex align-center">
                <div class="d-flex align-center" style="gap: 12px">
                  <v-avatar size="64" tile color="grey lighten-4">
                    <v-img
                      v-if="logoPreview"
                      :src="logoPreview"
                      contain
                    ></v-img>
                    <v-icon v-else color="grey">mdi-image-off-outline</v-icon>
                  </v-avatar>
                  <div
                    class="grey--text text--darken-1"
                    style="font-size: 12px"
                  >
                    Logo preview
                  </div>
                </div>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.bet_amount"
                  label="Bet Amount"
                  :rules="[rules.required, rules.decimal]"
                  hide-details="auto"
                  outlined
                  dense
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.jackpot_prize"
                  label="Jackpot Prize"
                  :rules="[rules.required, rules.decimal]"
                  hide-details="auto"
                  outlined
                  dense
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.second_prize"
                  label="Second Prize"
                  :rules="[rules.required, rules.decimal]"
                  hide-details="auto"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.third_prize"
                  label="Third Prize"
                  :rules="[rules.required, rules.decimal]"
                  hide-details="auto"
                  outlined
                  dense
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.fourth_prize"
                  label="Fourth Prize"
                  :rules="[rules.required, rules.decimal]"
                  hide-details="auto"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.pool_percentage"
                  label="Pool Percentage"
                  :rules="[rules.required, rules.decimal]"
                  suffix="%"
                  hide-details="auto"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>

            <div class="text-right mt-4">
              <v-btn depressed class="mr-2" @click="closeDialog">Cancel</v-btn>
              <v-btn color="primary" depressed type="submit" :loading="saving">
                {{ isEditing ? "Update" : "Save" }}
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <Snackbar ref="snackbar" />
  </div>
</template>

<script>
import { mapState } from "vuex";
import formatDate from "@/helpers/formatDate";
import formRules from "@/helpers/formRules";
import responseGet from "@/helpers/api_request_get";
import responsePost from "@/helpers/api_request_post";
import Snackbar from "@/components/Snackbar";

const initialForm = () => ({
  id: null,
  name: "",
  logo: null,
  bet_amount: "",
  jackpot_prize: "",
  second_prize: "",
  third_prize: "",
  fourth_prize: "",
  pool_percentage: "",
  status: "Active",
  currentLogo: "",
});

export default {
  components: {
    Snackbar,
  },

  data: () => ({
    rules: formRules,
    valid: true,
    loading: false,
    saving: false,
    dialog: false,
    isEditing: false,
    search: "",
    sortBy: "updated_at",
    sortDesc: true,
    records: [],
    form: initialForm(),
    formError: "",
    logoPreview: "",
    statusOptions: ["Active", "Inactive"],
    headers: [
      {
        text: "Action",
        value: "action",
        class: "text-uppercase",
        sortable: false,
        align: "center",
      },
      { text: "Logo", value: "logo", class: "text-uppercase", sortable: false },
      { text: "Name", value: "name", class: "text-uppercase" },
      { text: "Bet Amount", value: "bet_amount", class: "text-uppercase" },
      {
        text: "Jackpot Prize",
        value: "jackpot_prize",
        class: "text-uppercase",
      },
      {
        text: "Second Prize",
        value: "second_prize",
        class: "text-uppercase",
      },
      {
        text: "Third Prize",
        value: "third_prize",
        class: "text-uppercase",
      },
      {
        text: "Fourth Prize",
        value: "fourth_prize",
        class: "text-uppercase",
      },
      {
        text: "Pool Percentage",
        value: "pool_percentage",
        class: "text-uppercase",
      },
      { text: "Status", value: "status", class: "text-uppercase" },
      { text: "Updated At", value: "updated_at", class: "text-uppercase" },
    ],
  }),

  computed: {
    ...mapState(["accountInfo"]),

    canManage() {
      return (
        this.accountInfo &&
        this.accountInfo.access &&
        this.accountInfo.access.includes(32)
      );
    },
  },

  created() {
    this.loadRecords();
  },

  methods: {
    formatTheDate(value) {
      return formatDate(value, true);
    },

    moneyFormat(value) {
      const amount = Number(value || 0);

      return `PHP ${amount.toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },

    percentFormat(value) {
      const amount = Number(value || 0);

      return `${amount.toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`;
    },

    async loadRecords() {
      try {
        this.loading = true;

        const response = await responseGet("", "/otso_panalo/game_configs");

        if (response.status === 200) {
          this.records = response.data.records || [];
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          this.$store.commit(
            "setRefreshError",
            "Failed to load Otso Panalo game configs.",
          );
        }
      } catch (error) {
        this.$store.commit(
          "setRefreshError",
          "Failed to load Otso Panalo game configs.",
        );
      } finally {
        this.loading = false;
      }
    },

    openCreateDialog() {
      this.isEditing = false;
      this.form = initialForm();
      this.formError = "";
      this.logoPreview = "";
      this.dialog = true;
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
      });
    },

    openEditDialog(item) {
      this.isEditing = true;
      this.form = {
        id: item.id,
        name: item.name || "",
        logo: null,
        bet_amount: item.bet_amount || "",
        jackpot_prize: item.jackpot_prize || "",
        second_prize: item.second_prize || "",
        third_prize: item.third_prize || "",
        fourth_prize: item.fourth_prize || "",
        pool_percentage: item.pool_percentage || "",
        status: item.status || "Active",
        currentLogo: item.logo || "",
      };
      this.formError = "";
      this.logoPreview = item.logo || "";
      this.dialog = true;
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
      });
    },

    closeDialog(force = false) {
      if (this.saving && !force) {
        return;
      }

      this.dialog = false;
      this.form = initialForm();
      this.formError = "";
      this.logoPreview = "";
    },

    onLogoChange(file) {
      if (file) {
        this.logoPreview = URL.createObjectURL(file);
      }
    },

    clearLogoPreview() {
      this.logoPreview = this.form.currentLogo || "";
    },

    buildFormData() {
      const formData = new FormData();

      if (this.isEditing) {
        formData.append("id", this.form.id);
      }

      formData.append("name", this.form.name);
      formData.append("logo", this.form.logo || "");
      formData.append("bet_amount", this.form.bet_amount);
      formData.append("jackpot_prize", this.form.jackpot_prize);
      formData.append("second_prize", this.form.second_prize);
      formData.append("third_prize", this.form.third_prize);
      formData.append("fourth_prize", this.form.fourth_prize);
      formData.append("pool_percentage", this.form.pool_percentage);
      formData.append("status", this.form.status);

      return formData;
    },

    firstErrorMessage(response) {
      if (!response || !response.data) {
        return "Failed to save Otso Panalo game config.";
      }

      if (response.data.message && typeof response.data.message === "string") {
        return response.data.message;
      }

      const errorData = response.data.messages || response.data;

      if (errorData && typeof errorData === "object") {
        const firstValue = Object.values(errorData).find(
          (value) => typeof value === "string" && value.length,
        );

        if (firstValue) {
          return firstValue;
        }
      }

      return "Failed to save Otso Panalo game config.";
    },

    async submitForm() {
      this.formError = "";

      if (!this.$refs.form.validate()) {
        return;
      }

      try {
        this.saving = true;

        const endpoint = this.isEditing
          ? "otso_panalo/game_configs/update"
          : "otso_panalo/game_configs";

        const response = await responsePost(this.buildFormData(), endpoint);

        if (response.status === 200) {
          this.closeDialog(true);
          await this.loadRecords();
          this.$refs.snackbar.show(
            "success",
            "mdi-check-circle-outline",
            this.isEditing
              ? "Otso Panalo game config updated successfully."
              : "Otso Panalo game config created successfully.",
            4000,
          );
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          this.formError = this.firstErrorMessage(response);
        }
      } catch (error) {
        this.formError = "Failed to save Otso Panalo game config.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
